import React from 'react';
import { Link } from 'react-router-dom';
import {
    Server,
    Cloud,
    Network,
    RefreshCw,
    Zap,
    Shield,
    TrendingUp,
    ArrowRight,
    CheckCircle2,
    Cpu,
    Database,
    Settings
} from 'lucide-react';
import PageLayout from '../../Layout/PageLayout';
import PageHeader from '../../PageHeader/PageHeader';
import ModernCTA from '../../CTA/ModernCTA';
import './ModernSolutions.scss';

const ModernItModernization = () => {
    const services = [
        {
            icon: <Server size={28} />,
            title: 'Server/Workstation Refreshes',
            description: 'Upgrade your hardware infrastructure with cutting-edge servers and workstations for optimal performance.',
            features: ['Latest hardware', 'Performance boost', 'Extended warranty', 'Migration support']
        },
        {
            icon: <Cloud size={28} />,
            title: 'Office 365/G Suite Migrations',
            description: 'Seamlessly migrate to cloud-based productivity suites with zero downtime and full data integrity.',
            features: ['Zero downtime', 'Data integrity', 'User training', 'Post-migration support']
        },
        {
            icon: <Network size={28} />,
            title: 'Software-Defined Networking',
            description: 'Implement flexible, scalable network solutions that adapt to your business requirements.',
            features: ['Scalable design', 'Central management', 'Cost reduction', 'Enhanced security']
        },
        {
            icon: <RefreshCw size={28} />,
            title: 'Legacy Application Modernization',
            description: 'Transform outdated applications into modern, efficient systems that drive business growth.',
            features: ['Code refactoring', 'Cloud-native', 'API integration', 'Improved UX']
        },
        {
            icon: <Database size={28} />,
            title: 'Data Center Consolidation',
            description: 'Optimize your data center footprint while improving efficiency and reducing costs.',
            features: ['Space optimization', 'Energy savings', 'Improved cooling', 'Better reliability']
        },
        {
            icon: <Settings size={28} />,
            title: 'Infrastructure Automation',
            description: 'Automate routine infrastructure tasks to increase efficiency and reduce human error.',
            features: ['CI/CD pipelines', 'Config management', 'Automated testing', 'Self-healing systems']
        }
    ];

    const outcomes = [
        {
            icon: <Zap size={28} />,
            title: 'Faster Operations',
            description: 'Significantly improved system performance and response times'
        },
        {
            icon: <Shield size={28} />,
            title: 'Reduced Downtime',
            description: 'Modern infrastructure means fewer failures and faster recovery'
        },
        {
            icon: <TrendingUp size={28} />,
            title: 'Future-Ready',
            description: 'Infrastructure designed to scale with your business needs'
        }
    ];

    const features = [
        { icon: <Cpu size={24} />, title: 'Modern', text: 'Latest technology' },
        { icon: <Shield size={24} />, title: 'Secure', text: 'Enterprise security' },
        { icon: <Cloud size={24} />, title: 'Scalable', text: 'Grows with you' },
        { icon: <Zap size={24} />, title: 'Fast', text: 'Quick deployment' }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="IT Modernization"
                subtitle="Bridging Business Needs with Technology Innovation"
                breadcrumbs={[
                    { label: 'Solutions', path: '/solutions' },
                    { label: 'IT Modernization' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-solutions-hero">
                <div className="jk-solutions-hero__container">
                    <div className="jk-solutions-hero__grid">
                        <div className="jk-solutions-hero__content">
                            <span className="jk-section-label">
                                <Server size={16} />
                                Transform Your Infrastructure
                            </span>
                            <h2 className="jk-solutions-hero__title">
                                Transform Outdated Systems into
                                <span className="highlight"> Efficient Digital Operations</span>
                            </h2>
                            <p className="jk-solutions-hero__description">
                                We transform outdated systems into efficient digital operations. Our team
                                upgrades hardware, migrates data to secure cloud environments, and implements
                                modern networks that scale with your growth.
                            </p>
                            <div className="jk-solutions-hero__buttons">
                                <Link to="/contact" className="jk-btn jk-btn--primary">
                                    Start Your Transformation
                                    <ArrowRight size={20} />
                                </Link>
                                <Link to="/service" className="jk-btn jk-btn--secondary">
                                    View All Services
                                </Link>
                            </div>
                        </div>

                        <div className="jk-solutions-hero__features">
                            {features.map((feature, index) => (
                                <div key={index} className="jk-solutions-hero__feature-card">
                                    <div className="jk-solutions-hero__feature-icon">
                                        {feature.icon}
                                    </div>
                                    <h4 className="jk-solutions-hero__feature-title">{feature.title}</h4>
                                    <p className="jk-solutions-hero__feature-text">{feature.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="jk-solutions-services">
                <div className="jk-solutions-services__container">
                    <div className="jk-solutions-services__header">
                        <span className="jk-section-label">
                            <Settings size={16} />
                            Our Solutions
                        </span>
                        <h2 className="jk-solutions-services__title">
                            Comprehensive Modernization
                            <span className="highlight"> Services</span>
                        </h2>
                        <p className="jk-solutions-services__subtitle">
                            End-to-end IT modernization solutions designed to transform your infrastructure
                        </p>
                    </div>

                    <div className="jk-solutions-services__grid">
                        {services.map((service, index) => (
                            <div key={index} className="jk-solutions-services__card">
                                <div className="jk-solutions-services__card-icon">
                                    {service.icon}
                                </div>
                                <h3 className="jk-solutions-services__card-title">{service.title}</h3>
                                <p className="jk-solutions-services__card-description">{service.description}</p>
                                <ul className="jk-solutions-services__card-features">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={12} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Outcomes Section */}
            <section className="jk-solutions-outcomes">
                <div className="jk-solutions-outcomes__container">
                    <div className="jk-solutions-outcomes__header">
                        <h2 className="jk-solutions-outcomes__title">
                            Results You Can Expect
                        </h2>
                    </div>

                    <div className="jk-solutions-outcomes__grid">
                        {outcomes.map((outcome, index) => (
                            <div key={index} className="jk-solutions-outcomes__card">
                                <div className="jk-solutions-outcomes__card-icon">
                                    {outcome.icon}
                                </div>
                                <h3 className="jk-solutions-outcomes__card-title">{outcome.title}</h3>
                                <p className="jk-solutions-outcomes__card-text">{outcome.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernItModernization;
